import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { fitImageStyle } from './useFullscreenStage';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { NID_CARD_ASPECT_RATIO, NID_CARD_CROP_OUTPUT_PX } from '../../image/nid-to-pdf/nidCardConstants';
import { isConvexQuad, orderQuadCorners, warpQuadToRect } from '../../image/shared/perspectiveHomography';
import { detectCardQuadFromImage } from '../../image/nid-to-pdf/detectCardQuad';

const HANDLE = 7;
const STROKE = 2;
const FILL = 'rgba(0, 120, 255, 0.12)';

/** @typedef {[number, number]} Pt */

function defaultCorners(nw, nh) {
    const R = NID_CARD_ASPECT_RATIO;
    const mx = nw * 0.08;
    const my = nh * 0.08;
    const innerW = nw - 2 * mx;
    const innerH = nh - 2 * my;
    let rectW;
    let rectH;
    if (innerH * R <= innerW) {
        rectH = innerH;
        rectW = innerH * R;
    } else {
        rectW = innerW;
        rectH = innerW / R;
    }
    const left = mx + (innerW - rectW) / 2;
    const top = my + (innerH - rectH) / 2;
    const right = left + rectW;
    const bottom = top + rectH;
    return [
        [left, top],
        [right, top],
        [right, bottom],
        [left, bottom],
    ];
}

/** A stored quad is only usable if it still fits the image it was drawn on. */
function usableQuad(quad, w, h) {
    return Array.isArray(quad)
        && quad.length === 4
        && quad.every((p) => Array.isArray(p) && p.length === 2
            && Number.isFinite(p[0]) && Number.isFinite(p[1])
            && p[0] >= 0 && p[0] <= w && p[1] >= 0 && p[1] <= h);
}

const NidQuadCropPanel = forwardRef(function NidQuadCropPanel({ imageSrc, initialQuad, onLoaded, fitBox }, ref) {
    const { t } = useTranslation();
    const wrapRef = useRef(null);
    const imgRef = useRef(null);
    const [nw, setNw] = useState(0);
    const [nh, setNh] = useState(0);
    const [box, setBox] = useState({ w: 0, h: 0 });

    /** @type {React.MutableRefObject<Pt[]>} */
    const cornersRef = useRef([
        [0, 0],
        [100, 0],
        [100, 100],
        [0, 100],
    ]);
    const [, bump] = useState(0);
    const force = () => bump((x) => x + 1);

    const dragRef = useRef(null);
    const layoutRef = useRef({ scale: 1 });

    const measure = useCallback(() => {
        const img = imgRef.current;
        if (!img?.naturalWidth) return;
        const rect = img.getBoundingClientRect();
        const nw0 = img.naturalWidth;
        const scale = rect.width / nw0;
        layoutRef.current = { scale };
        setBox({ w: rect.width, h: rect.height });
    }, []);

    const natToDisp = useCallback(([nx, ny]) => {
        const { scale } = layoutRef.current;
        return [nx * scale, ny * scale];
    }, []);

    const dispToNat = useCallback(
        ([dx, dy]) => {
            const { scale } = layoutRef.current;
            return [
                Math.max(0, Math.min(nw || 1, dx / scale)),
                Math.max(0, Math.min(nh || 1, dy / scale)),
            ];
        },
        [nw, nh],
    );

    const onImgLoad = useCallback(() => {
        const img = imgRef.current;
        if (!img) return;
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        setNw(w);
        setNh(h);
        // Corners the user already set win over a fresh detection: reopening the
        // crop should carry on from the saved frame, not start over.
        if (usableQuad(initialQuad, w, h)) {
            cornersRef.current = initialQuad.map((p) => [...p]);
        } else {
            let detected = null;
            try {
                detected = detectCardQuadFromImage(img)?.quad ?? null;
            } catch {
                detected = null;
            }
            cornersRef.current = detected && detected.length === 4 ? detected : defaultCorners(w, h);
        }
        // Detection hands back a quad cycled to force a landscape result, which
        // would leave the on-screen top-left dot acting as some other corner and
        // silently turn the crop. Here the dots keep fixed roles — turning the
        // card is the rotate control's job, not the crop's.
        cornersRef.current = orderQuadCorners(cornersRef.current);
        requestAnimationFrame(() => {
            measure();
            force();
            onLoaded?.();
        });
    }, [initialQuad, measure, onLoaded]);

    useEffect(() => {
        const img = imgRef.current;
        if (!img) return;
        const ro = new ResizeObserver(() => {
            measure();
            force();
        });
        ro.observe(img);
        return () => ro.disconnect();
    }, [imageSrc, measure]);

    const dragMatchesPointer = useCallback((e, d) => {
        if (!d) return false;
        if (d.pointerType === 'mouse' && e.pointerType === 'mouse') return true;
        return d.pointerId === e.pointerId;
    }, []);

    useEffect(() => {
        const onMove = (e) => {
            const d = dragRef.current;
            if (!d || !dragMatchesPointer(e, d)) return;
            const img = imgRef.current;
            if (!img) return;
            const r = img.getBoundingClientRect();
            const lx = e.clientX - r.left;
            const ly = e.clientY - r.top;
            if (d.kind === 'corner') {
                cornersRef.current[d.cornerIndex] = dispToNat([lx, ly]);
            } else if (d.kind === 'pan') {
                const curNat = dispToNat([lx, ly]);
                const dx = curNat[0] - d.startNat[0];
                const dy = curNat[1] - d.startNat[1];
                const nw0 = nw || 1;
                const nh0 = nh || 1;
                cornersRef.current = d.startCorners.map(([x, y]) => [
                    Math.max(0, Math.min(nw0 - 1, x + dx)),
                    Math.max(0, Math.min(nh0 - 1, y + dy)),
                ]);
            }
            force();
        };
        const onUp = (e) => {
            const d = dragRef.current;
            if (!d || !dragMatchesPointer(e, d)) return;
            dragRef.current = null;
            // Dragging one dot past another would leave the corners in a rotated
            // (or crossed) order; re-anchoring them here keeps every dot tied to
            // the corner it visually sits on.
            if (d.kind === 'corner') {
                cornersRef.current = orderQuadCorners(cornersRef.current);
                force();
            }
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
        return () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('pointercancel', onUp);
        };
    }, [dispToNat, dragMatchesPointer, nw, nh]);

    const onPointerDownHandle = useCallback((cornerIndex) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = {
            kind: 'corner',
            cornerIndex,
            pointerId: e.pointerId,
            pointerType: e.pointerType,
        };
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }, []);

    const onPointerDownPan = useCallback(
        (e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            e.preventDefault();
            const img = imgRef.current;
            if (!img || !nw || !nh) return;
            const r = img.getBoundingClientRect();
            const lx = e.clientX - r.left;
            const ly = e.clientY - r.top;
            dragRef.current = {
                kind: 'pan',
                pointerId: e.pointerId,
                pointerType: e.pointerType,
                startNat: dispToNat([lx, ly]),
                startCorners: cornersRef.current.map((p) => [...p]),
            };
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        },
        [dispToNat, nw, nh],
    );

    useImperativeHandle(
        ref,
        () => ({
            /** Current corners in natural-image coordinates, TL TR BR BL. */
            getQuad() {
                return orderQuadCorners(cornersRef.current).map((p) => [...p]);
            },
            async getWarpedJpegBlob() {
                const img = imgRef.current;
                if (!img?.naturalWidth) throw new Error('no image');
                const quad = orderQuadCorners(cornersRef.current).map((p) => [...p]);
                if (!isConvexQuad(quad)) {
                    throw new Error('invalid quad');
                }
                const { w: tw, h: th } = NID_CARD_CROP_OUTPUT_PX;
                const interimW = Math.min(2048, Math.max(tw * 2, img.naturalWidth));
                const interimH = Math.round(interimW / NID_CARD_ASPECT_RATIO);
                const warped = warpQuadToRect(img, quad, interimW, interimH);
                const out = document.createElement('canvas');
                out.width = tw;
                out.height = th;
                const ctx = out.getContext('2d');
                if (!ctx) throw new Error('canvas');
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, tw, th);
                ctx.drawImage(warped, 0, 0, tw, th);
                return await new Promise((resolve, reject) => {
                    out.toBlob(
                        (blob) => {
                            if (blob) resolve(blob);
                            else reject(new Error('blob'));
                        },
                        'image/jpeg',
                        0.92,
                    );
                });
            },
        }),
        [],
    );

    const corners = cornersRef.current;
    const d0 = natToDisp(corners[0]);
    const d1 = natToDisp(corners[1]);
    const d2 = natToDisp(corners[2]);
    const d3 = natToDisp(corners[3]);
    const polyPts = `${d0[0]},${d0[1]} ${d1[0]},${d1[1]} ${d2[0]},${d2[1]} ${d3[0]},${d3[1]}`;

    return (
        <div ref={wrapRef} className="relative inline-block max-w-full select-none touch-none">
            <img
                ref={imgRef}
                src={imageSrc}
                alt=""
                draggable={false}
                onLoad={onImgLoad}
                // Fitted to the modal's measured stage, aspect ratio kept — the
                // whole photo in view, nothing to scroll (see useFullscreenStage).
                style={fitImageStyle(fitBox)}
                className="block object-contain"
            />
            {nw > 0 && nh > 0 && box.w > 0 ? (
                <svg
                    width={box.w}
                    height={box.h}
                    className="pointer-events-none absolute left-0 top-0 overflow-visible"
                >
                    <polygon
                        points={polyPts}
                        fill={FILL}
                        stroke="#0288d1"
                        strokeWidth={STROKE}
                        style={{ pointerEvents: 'visiblePainted', cursor: 'move' }}
                        onPointerDown={onPointerDownPan}
                    />
                    {/* Which edge becomes the TOP of the cropped card, drawn on that
                        edge itself: with the card on its side the dots alone do not
                        say. */}
                    <g
                        transform={`translate(${(d0[0] + d1[0]) / 2} ${(d0[1] + d1[1]) / 2}) rotate(${(Math.atan2(d1[1] - d0[1], d1[0] - d0[0]) * 180) / Math.PI})`}
                        style={{ pointerEvents: 'none' }}
                    >
                        <rect x={-22} y={-9} width={44} height={18} rx={9} fill="#0288d1" />
                        <text x={0} y={4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#fff">
                            {t('nidToPdf.crop.topEdge')}
                        </text>
                    </g>
                    {[d0, d1, d2, d3].map((p, i) => (
                        <circle
                            key={i}
                            cx={p[0]}
                            cy={p[1]}
                            r={HANDLE}
                            fill="#fff"
                            stroke="#ff9800"
                            strokeWidth={2}
                            style={{ pointerEvents: 'all', cursor: 'grab' }}
                            onPointerDown={onPointerDownHandle(i)}
                        />
                    ))}
                </svg>
            ) : null}
        </div>
    );
});

export default NidQuadCropPanel;
